import {EntityIdentifier} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Modal, ModalProps, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {EntitySelect} from "../../select/entity/EntitySelect/EntitySelect";
import {EntityTypeSelect} from "../../select/entity/EntityTypeSelect/EntityTypeSelect";
import {ObjectMappingKindSelect} from "../../select/entity/ObjectMappingKindSelect/ObjectMappingKindSelect";

interface ObjectMappingFormProps {
  options?: ObjectMappingFormOptions;
  defaultValues?: Partial<ObjectMappingFormValues>;
  onSubmit?: (values: ObjectMappingFormValues) => void;
  submitting?: boolean;
}

export interface ObjectMappingFormOptions {
  regionId?: string
  objectSelectDisabled?: boolean
}

export interface ObjectMappingFormValues {
  kind: string
  key: string
  value: string
  object: EntityIdentifier
}

const getDefaultValues = (defaultValues: Partial<ObjectMappingFormValues> | undefined) => {
  return {
    kind: defaultValues?.kind,
    key: defaultValues?.key,
    value: defaultValues?.value,
    object: defaultValues?.object,
  }
}

export const ObjectMappingForm = (props: ObjectMappingFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        kind: values.kind!,
        key: values.key!,
        value: values.value!,
        object: {
          id: values.object!.id,
          type: values.object!.type
        }
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <ObjectMappingKindSelect
        label={t("common.entity.objectmapping.form.field.kind", "Kind")}
        placeholder={t<string>("common.entity.objectmapping.form.field.kind.placeholder", "Kind")}
        styles={{wrapper: {width: "50%"}}}
        withAsterisk
        required
        {...form.getInputProps("kind")} />
      <TextInput
        label={t("common.entity.objectmapping.form.field.key", "Key")}
        placeholder={t<string>("common.entity.objectmapping.form.field.key.placeholder", "Key")}
        styles={{wrapper: {width: "50%"}}}
        withAsterisk
        required
        {...form.getInputProps("key")} />
      <TextInput
        label={t("common.entity.objectmapping.form.field.value", "Value")}
        placeholder={t<string>("common.entity.objectmapping.form.field.value.placeholder", "Value")}
        styles={{wrapper: {width: "50%"}}}
        withAsterisk
        required
        {...form.getInputProps("value")} />
      <Divider my="sm" variant="dotted"/>
      <EntityTypeSelect
        label={t("common.entity.objectmapping.form.field.object", "Object")}
        placeholder={t<string>("common.entity.objectmapping.form.field.object.placeholder", "Object")}
        disabled={options?.objectSelectDisabled}
        withAsterisk
        required
        clearable
        {...form.getInputProps("object.type")} />
      <EntitySelect
        pt="md"
        disabled={options?.objectSelectDisabled}
        entityType={form.getTransformedValues().object?.type}
        regionId={options?.regionId}
        withAsterisk
        required
        clearable
        {...form.getInputProps("object.id")} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface ObjectMappingFormModalProps extends ObjectMappingFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const ObjectMappingFormModal = (props: ObjectMappingFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <ObjectMappingForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
